import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import Select from 'react-select';
import FormInput from '../../../components/ui/FormInput';
import { CurrencyDollarIcon, UserGroupIcon } from '@heroicons/react/24/outline';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const ReservationBasicInfo = ({ formData, onChange, agencies, regions, vehicleTypes, user }) => {
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    onChange({
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSelectChange = (selectedOption, { name }) => {
    onChange({
      [name]: selectedOption ? selectedOption.value : ''
    });
  };

  const handleSwitchChange = (checked) => {
    onChange({ isRoundTrip: checked });
  };

  // Fiyat hesaplama fonksiyonu
  const calculatePrice = () => {
    if (formData.vehicleTypeId && formData.fromRegionId && formData.toRegionId) {
      const selectedVehicleType = vehicleTypes.find(vt => vt.id.toString() === formData.vehicleTypeId.toString());
      const basePrice = selectedVehicleType ? selectedVehicleType.capacity * 10 : 0;
      
      if (formData.isRoundTrip) {
        const arrivalPrice = basePrice;
        const returnPrice = basePrice * 0.8;
        const totalPrice = arrivalPrice + returnPrice;
        setCalculatedPrice(totalPrice);
        onChange({ 
          arrivalPrice: arrivalPrice,
          returnPrice: returnPrice,
          price: totalPrice
        });
      } else {
        const arrivalPrice = basePrice;
        setCalculatedPrice(arrivalPrice);
        onChange({ 
          arrivalPrice: arrivalPrice,
          returnPrice: 0,
          price: arrivalPrice
        });
      }
    }
  };

  // Araç tipi, bölgeler veya gidiş-dönüş seçeneği değiştiğinde fiyatı hesapla
  useEffect(() => {
    if (formData.vehicleTypeId && formData.fromRegionId && formData.toRegionId) {
      calculatePrice();
    }
  }, [formData.vehicleTypeId, formData.fromRegionId, formData.toRegionId, formData.isRoundTrip]);

  // Select options
  const agencyOptions = agencies.map(agency => ({ value: agency.id, label: agency.name }));
  const regionOptions = regions.map(region => ({ value: region.id, label: region.name }));
  const vehicleTypeOptions = vehicleTypes.map(type => ({ 
    value: type.id, 
    label: `${type.name} (Kapasite: ${type.capacity})` 
  }));
  const currencyOptions = [
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' },
    { value: 'TRY', label: 'TRY' },
    { value: 'GBP', label: 'GBP' }
  ];

  // Custom styles for react-select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
      boxShadow: state.isFocused ? '0 0 0 2px #DBEAFE' : 'none',
      borderRadius: '0.5rem',
      padding: '2px',
      '&:hover': {
        borderColor: state.isFocused ? '#3B82F6' : '#9CA3AF',
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#DBEAFE' : null,
      color: state.isSelected ? 'white' : '#111827',
    }),
  };

  return (
    <div className="space-y-6">
      {/* İlk satır: Acente, Acente Referans No, Gidiş-Dönüş */}
      <div className="grid grid-cols-12 gap-4 items-end">
        <div className="col-span-4">
          <label htmlFor="agencyId" className="block text-sm font-medium text-gray-700 mb-1.5">
            Acente *
          </label>
          <Select
            id="agencyId"
            name="agencyId"
            options={agencyOptions}
            value={agencyOptions.find(option => option.value.toString() === formData.agencyId?.toString())}
            onChange={(option) => handleSelectChange(option, { name: 'agencyId' })}
            placeholder="Acente seçiniz"
            isClearable={user?.role !== 'agency_user'}
            isSearchable
            isDisabled={user?.role === 'agency_user'}
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        <div className="col-span-4">
          <FormInput
            id="agencyReference"
            name="agencyReference"
            label="Acente Referans No"
            value={formData.agencyReference}
            onChange={handleChange}
            placeholder="Acente referans numarası"
          />
        </div>

        <div className="col-span-4">
          <label htmlFor="isRoundTrip" className="block text-sm font-medium text-gray-700 mb-1.5">
            Seyahat Tipi
          </label>
          <Select
            id="isRoundTrip"
            name="isRoundTrip"
            options={[
              { value: false, label: 'Tek Yön' },
              { value: true, label: 'Gidiş-Dönüş' }
            ]}
            value={formData.isRoundTrip ? { value: true, label: 'Gidiş-Dönüş' } : { value: false, label: 'Tek Yön' }}
            onChange={(option) => onChange({ isRoundTrip: option ? option.value : false })}
            placeholder="Seyahat tipi seçiniz"
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>
      </div>

      {/* İkinci satır: Başlangıç Bölgesi, Varış Bölgesi, Otel, İkinci Otel */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3">
          <label htmlFor="fromRegionId" className="block text-sm font-medium text-gray-700 mb-1.5">
            Başlangıç Bölgesi *
          </label>
          <Select
            id="fromRegionId"
            name="fromRegionId"
            options={regionOptions}
            value={regionOptions.find(option => option.value.toString() === formData.fromRegionId?.toString())}
            onChange={(option) => handleSelectChange(option, { name: 'fromRegionId' })}
            placeholder="Başlangıç bölgesi seçiniz"
            isClearable
            isSearchable
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        <div className="col-span-3">
          <label htmlFor="toRegionId" className="block text-sm font-medium text-gray-700 mb-1.5">
            Varış Bölgesi *
          </label>
          <Select
            id="toRegionId"
            name="toRegionId"
            options={regionOptions}
            value={regionOptions.find(option => option.value.toString() === formData.toRegionId?.toString())}
            onChange={(option) => handleSelectChange(option, { name: 'toRegionId' })}
            placeholder="Varış bölgesi seçiniz"
            isClearable
            isSearchable
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        <div className="col-span-3">
          <FormInput
            id="hotelName"
            name="hotelName"
            label="Otel"
            value={formData.hotelName || ''}
            onChange={handleChange}
            placeholder="Otel adı giriniz"
          />
        </div>

        <div className="col-span-3">
          <FormInput
            id="secondaryHotelName"
            name="secondaryHotelName"
            label="İkinci Otel"
            value={formData.secondaryHotelName || ''}
            onChange={handleChange}
            placeholder="İkinci otel adı giriniz (opsiyonel)"
          />
        </div>
      </div>

      {/* Üçüncü satır: Araç Tipi, Yolcu Sayısı, Fiyat, Para Birimi */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3">
          <label htmlFor="vehicleTypeId" className="block text-sm font-medium text-gray-700 mb-1.5">
            Araç Tipi *
          </label>
          <Select
            id="vehicleTypeId"
            name="vehicleTypeId"
            options={vehicleTypeOptions}
            value={vehicleTypeOptions.find(option => option.value.toString() === formData.vehicleTypeId?.toString())}
            onChange={(option) => handleSelectChange(option, { name: 'vehicleTypeId' })}
            placeholder="Araç tipi seçiniz"
            isClearable
            isSearchable
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        <div className="col-span-3">
          <FormInput
            id="passengerCount"
            name="passengerCount"
            label="Yolcu Sayısı *"
            type="number"
            min="1"
            max="50"
            value={formData.passengerCount}
            onChange={handleChange}
            required
            icon={<UserGroupIcon className="h-5 w-5 text-gray-400" />}
          />
        </div>

        {formData.isRoundTrip ? (
          <>
            <div className="col-span-3">
              <FormInput
                id="arrivalPrice"
                name="arrivalPrice"
                label="Gidiş Fiyatı *"
                type="number"
                min="0"
                step="0.01"
                value={formData.arrivalPrice}
                onChange={(e) => {
                  const val = parseFloat(e.target.value || 0);
                  onChange({ 
                    arrivalPrice: val,
                    price: val + parseFloat(formData.returnPrice || 0)
                  });
                }}
                required
                icon={<span className="text-gray-500 sm:text-sm">{formData.currency}</span>}
              />
            </div>
            <div className="col-span-3">
              <FormInput
                id="returnPrice"
                name="returnPrice"
                label="Dönüş Fiyatı *"
                type="number"
                min="0"
                step="0.01"
                value={formData.returnPrice}
                onChange={(e) => {
                  const val = parseFloat(e.target.value || 0);
                  onChange({ 
                    returnPrice: val,
                    price: parseFloat(formData.arrivalPrice || 0) + val
                  });
                }}
                required
                icon={<span className="text-gray-500 sm:text-sm">{formData.currency}</span>}
              />
            </div>
          </>
        ) : (
          <div className="col-span-3">
            <FormInput
              id="arrivalPrice"
              name="arrivalPrice"
              label="Fiyat *"
              type="number"
              min="0"
              step="0.01"
              value={formData.arrivalPrice}
              onChange={(e) => {
                const val = parseFloat(e.target.value || 0);
                onChange({ 
                  arrivalPrice: val,
                  price: val
                });
              }}
              required
              icon={<span className="text-gray-500 sm:text-sm">{formData.currency}</span>}
              helpText={calculatedPrice !== null ? `Hesaplanan fiyat: ${calculatedPrice.toFixed(2)} ${formData.currency}` : null}
            />
          </div>
        )}

        <div className="col-span-3">
          <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1.5">
            Para Birimi *
          </label>
          <Select
            id="currency"
            name="currency"
            options={currencyOptions}
            value={currencyOptions.find(option => option.value === formData.currency)}
            onChange={(option) => handleSelectChange(option, { name: 'currency' })}
            placeholder="Para birimi seçiniz"
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>
      </div>
    </div>
  );
};

export default ReservationBasicInfo;